'use client';

import {
  getProviderLabel,
  getSendEmailConnectionDisplay,
  type EmailConnection,
} from './emailConnection';

const BODY_PREVIEW_MAX_LENGTH = 400;

interface EmailApprovalPreviewProps {
  toolName: string;
  preview: any;
  selectedConnectionId: string | null;
  selectedProvider: string | null;
  selectedSignatureId: string | null;
  useAsDefault: boolean;
  useSignatureAsDefault: boolean;
  onSelectConnection: (connectionId: string, provider: string) => void;
  onSelectSignature: (signatureId: string | null) => void;
  onUseAsDefaultChange: (value: boolean) => void;
  onUseSignatureAsDefaultChange: (value: boolean) => void;
}

/**
 * Content summary + connection/signature selectors for outbound-email approvals.
 * Pure presentational: all selection state is owned by ToolApprovalCard.
 */
export function EmailApprovalPreview({
  toolName,
  preview,
  selectedConnectionId,
  selectedProvider,
  selectedSignatureId,
  useAsDefault,
  useSignatureAsDefault,
  onSelectConnection,
  onSelectSignature,
  onUseAsDefaultChange,
  onUseSignatureAsDefaultChange,
}: EmailApprovalPreviewProps) {
  if (!preview?.input) return null;

  const isTemplate = toolName === 'send_email_with_template';
  const emailInput = preview.input as {
    to?: string;
    subject?: string;
    body?: string;
    cc?: string;
    bcc?: string;
    connectionId?: string;
    provider?: string;
    signatureId?: string;
    templateId?: string;
    contactId?: string;
    variableOverrides?: Record<string, string>;
  };
  const connections = preview.emailContext?.connections as EmailConnection[] | undefined;
  const { fromLabel, providerLabel, connectionId } = getSendEmailConnectionDisplay(
    {
      connectionId: selectedConnectionId ?? emailInput.connectionId,
      provider: selectedProvider ?? emailInput.provider,
    },
    connections
  );
  const bodyText = !isTemplate && typeof emailInput.body === 'string' ? emailInput.body : '';
  const bodyPreview =
    bodyText.length > BODY_PREVIEW_MAX_LENGTH
      ? `${bodyText.slice(0, BODY_PREVIEW_MAX_LENGTH)}…`
      : bodyText;
  const signaturesByConnectionId = preview.emailContext?.signaturesByConnectionId as
    | Record<string, { id?: string; name?: string; is_default?: boolean }[]>
    | undefined;
  const signatures = connectionId ? signaturesByConnectionId?.[connectionId] : undefined;
  const effectiveSignatureId = selectedSignatureId ?? emailInput.signatureId;
  const signatureLabel =
    effectiveSignatureId && Array.isArray(signatures)
      ? signatures.find((s) => s.id === effectiveSignatureId)?.name ?? effectiveSignatureId
      : null;

  return (
    <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800 text-xs space-y-3">
      <p className="font-semibold text-blue-900 dark:text-blue-100">
        {isTemplate ? 'Templated email' : 'Email preview'}
      </p>

      {/* Connection selector */}
      {connections && connections.length > 0 && (
        <div className="rounded bg-blue-100/50 dark:bg-blue-800/30 p-2 space-y-2">
          <p className="font-medium text-blue-900 dark:text-blue-100">Send from</p>
          {connections.length > 1 ? (
            <select
              value={selectedConnectionId ?? ''}
              onChange={(e) => {
                const conn = connections.find((c) => c.connectionId === e.target.value);
                if (conn) onSelectConnection(conn.connectionId, conn.provider);
              }}
              className="w-full rounded border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-blue-900 dark:text-blue-100"
            >
              {connections.map((c) => (
                <option key={c.connectionId} value={c.connectionId}>
                  {c.email
                    ? `${c.email} (${getProviderLabel(c.provider)})`
                    : `${c.connectionId} (${getProviderLabel(c.provider)})`}
                </option>
              ))}
            </select>
          ) : (
            <p>
              <strong>From:</strong> {fromLabel}
            </p>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useAsDefault}
              onChange={(e) => onUseAsDefaultChange(e.target.checked)}
              className="rounded border-blue-300"
            />
            <span className="text-blue-900 dark:text-blue-100">
              Use this connection as default for future emails
            </span>
          </label>
        </div>
      )}

      {/* Signature selector */}
      {signatures && signatures.length > 0 && (
        <div className="rounded bg-blue-100/50 dark:bg-blue-800/30 p-2 space-y-2">
          <p className="font-medium text-blue-900 dark:text-blue-100">Signature</p>
          <select
            value={effectiveSignatureId ?? ''}
            onChange={(e) => onSelectSignature(e.target.value || null)}
            className="w-full rounded border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-blue-900 dark:text-blue-100"
          >
            <option value="">No signature</option>
            {signatures.map((s) => (
              <option key={s.id} value={s.id ?? ''}>
                {s.name ?? s.id} {s.is_default ? '(default)' : ''}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useSignatureAsDefault}
              onChange={(e) => onUseSignatureAsDefaultChange(e.target.checked)}
              className="rounded border-blue-300"
            />
            <span className="text-blue-900 dark:text-blue-100">
              Use this signature as default for this connection
            </span>
          </label>
        </div>
      )}

      {/* Connection metadata */}
      <div className="rounded bg-blue-100/50 dark:bg-blue-800/30 p-2 space-y-1">
        <p className="font-medium text-blue-900 dark:text-blue-100">Sending from</p>
        <p>
          <strong>From:</strong> {fromLabel}
        </p>
        <p>
          <strong>Connection:</strong> {connectionId}
        </p>
        <p>
          <strong>Provider:</strong> {providerLabel}
        </p>
        {signatureLabel && (
          <p>
            <strong>Signature:</strong> {signatureLabel}
          </p>
        )}
      </div>

      {/* Recipients and content */}
      <div className="space-y-1">
        {isTemplate ? (
          <>
            <p>
              <strong>Template ID:</strong> {emailInput.templateId ?? '—'}
            </p>
            {emailInput.contactId ? (
              <p>
                <strong>Contact ID:</strong> {emailInput.contactId}
              </p>
            ) : null}
            <p>
              <strong>To:</strong> {emailInput.to ?? '—'}
            </p>
            {emailInput.cc ? (
              <p>
                <strong>CC:</strong> {emailInput.cc}
              </p>
            ) : null}
            {emailInput.bcc ? (
              <p>
                <strong>BCC:</strong> {emailInput.bcc}
              </p>
            ) : null}
            {emailInput.variableOverrides && Object.keys(emailInput.variableOverrides).length > 0 ? (
              <div>
                <p className="font-medium">Variable overrides</p>
                <pre className="overflow-auto bg-white dark:bg-slate-900 p-2 rounded text-xs whitespace-pre-wrap border border-blue-100 dark:border-blue-800 max-h-32">
                  {JSON.stringify(emailInput.variableOverrides, null, 2)}
                </pre>
              </div>
            ) : null}
            <p className="text-blue-800 dark:text-blue-200 mt-2">
              Subject and body are rendered from the saved template when you approve (placeholders
              filled from the contact and overrides).
            </p>
          </>
        ) : (
          <>
            <p>
              <strong>To:</strong> {emailInput.to ?? '—'}
            </p>
            {emailInput.cc ? (
              <p>
                <strong>CC:</strong> {emailInput.cc}
              </p>
            ) : null}
            {emailInput.bcc ? (
              <p>
                <strong>BCC:</strong> {emailInput.bcc}
              </p>
            ) : null}
            <p>
              <strong>Subject:</strong> {emailInput.subject ?? '—'}
            </p>
            <p>
              <strong>Body:</strong>
            </p>
            <pre className="overflow-auto bg-white dark:bg-slate-900 p-2 rounded text-xs whitespace-pre-wrap border border-blue-100 dark:border-blue-800 max-h-48">
              {bodyPreview || '(empty)'}
            </pre>
          </>
        )}
      </div>
    </div>
  );
}
